"use strict"

import React, { useState, useEffect } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Button } from "react-bootstrap"
import PropTypes from "prop-types"
import moment from "moment"

import { LanzaTable, UniversalFilter } from "../../../shared_components/widgets/LanzaTable"
import { showModal, hideModal } from "../../../shared_components/utils"
import { API_STATUS } from "../../../shared_components/widgets/utils"
import InfoPopover from "../../../shared_components/widgets/InfoPopover"
import ModalUploadDataFile from "./modals/ModalUploadDataFile"
import ModalViewDataUploadLogs from "./modals/ModalViewDataUploadLogs"
import ModalEnterData from "./modals/ModalEnterData"
import ModalAddMethod from "./modals/ModalAddMethod"
import ModalPrintLabels from "./modals/ModalPrintLabels"
import SampleStatus from "../resources/SampleStatus"
import CancelMethodButton from "../resources/CancelMethodButton"
import ToggleMethodStatus from "../resources/ToggleMethodStatus"
import { withSampleConfig } from "../../../config/SampleConfigProvider"
import { LocaleFormattedDate } from "../../internationalization/LocaleFormatted"
import { useSensePageWidth } from "../SampleScheduleTab/FormSamplePoint"

/* Table to display and manage samples and data upload. */
const TableSampleAnalysisStatus = ({
  sampleTableRef,
  querySamples,
  sample_not_analyzed_status,
  adding_sample_methods_status,
  reloadSampleTable,
  updateMetrics,
  markSampleAsNotAnalyzed,
  reloadSampleRoundsTable,
  getCurrentSampleData,
  getSamplingPointMethods,
  addMethodsToSample,
  setDownloadDisabled,
  getReactors,
  sample_point_data,
  sample_source_types,
  user_permissions = [],
  can_print_skokie = false,
  is_slims_site = false,
}) => {
  const intl = useIntl()
  const pageWidth = useSensePageWidth()
  const [selectedSample, setSelectedSample] = useState(null)
  const [enterDataModal, setEnterDataModal] = useState(false)
  const [printLabelsModal, setPrintLabelsModal] = useState(false)
  const [uploadDataModal, setUploadDataModal] = useState(false)
  const [viewUploadLogsModal, setViewUploadLogsModal] = useState(false)
  const [addMethodModal, setAddMethodModal] = useState(false)
  const [availablePrinters, setAvailablePrinters] = useState({})

  // Alert user if issue was encountered during marking sample as 'not analyzed'
  useEffect(() => {
    if (sample_not_analyzed_status === API_STATUS.ERROR) {
      alert(
        intl.formatMessage({
          id: "samplemanagement.error.unable.to.save.data",
        }),
      )

      updateMetrics({ sample_not_analyzed_status: API_STATUS.IDLING })
    }

    if (adding_sample_methods_status === API_STATUS.ERROR) {
      alert(
        intl.formatMessage({
          id: "samplemanagement.error.unable.to.update.method",
        }),
      )

      updateMetrics({ adding_sample_methods_status: API_STATUS.IDLING })
    }
  }, [sample_not_analyzed_status, adding_sample_methods_status])

  const updateStatus_notAnalyzed = (event, sample) => {
    event.preventDefault()

    const remarks = prompt(
      intl.formatMessage({
        id: "samplemanagement.sample.will_not_be_analyzed.prompt",
      }) + ":",
    )

    if (remarks == null || remarks === "") {
      return false
    }

    markSampleAsNotAnalyzed(sample.id, remarks)
  }

  const _render_uploadStatus = (sample, uploadedFile) => {
    if (uploadedFile) {
      return (
        <>
          <b>
            <FormattedMessage id={"general.data.upload.status"} defaultMessage={"Data Upload Status"} />:
          </b>
          &nbsp;
          {uploadedFile["transformation_state"]}
          <br />
          <div style={{ marginTop: "5px" }}>
            <a
              className={"mylink"}
              style={{ fontSize: "12px" }}
              onClick={(event) => {
                event.preventDefault()
                setSelectedSample(sample)
                showModal(setViewUploadLogsModal)
              }}
            >
              <FormattedMessage id={"general.log"} description={"View Logs"} />
            </a>
            {uploadedFile["transformation_state"] !== "OK" && uploadedFile["output_file"] ? (
              <>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <a
                  className={"mylink"}
                  href={uploadedFile["output_file"]}
                  target={"_blank"}
                  style={{ fontSize: "12px" }}
                >
                  <FormattedMessage id={"samplemanagement.download.errors"} description={"Download Errors"} />
                </a>
              </>
            ) : (
              ""
            )}
          </div>
        </>
      )
    } else {
      return (
        <>
          <b>
            <FormattedMessage id={"general.data.upload.status"} defaultMessage={"Data Upload Status"} />:
          </b>
          &nbsp; -
        </>
      )
    }
  }

  const getLatestUploadedFile = (sample) => {
    const sampleFileList = sample["uploaded_files"] || []
    return sampleFileList.length > 0 ? sampleFileList[sampleFileList.length - 1] : null
  }

  const onHideModal = (setModalFunction) => {
    hideModal(setModalFunction)
    setSelectedSample(null)
  }

  const onSuccessfulDataUpdate = (setModalFunction) => {
    onHideModal(setModalFunction)
    reloadSampleRoundsTable()
    reloadSampleTable()
  }

  const onMethodsAdded = (setModalFunction, methods) => {
    onHideModal(setModalFunction)
    addMethodsToSample(selectedSample.id, methods)
    reloadSampleRoundsTable()
    reloadSampleTable()
  }

  const updateMetricsWithFilter = (columnName, value) => {
    updateMetrics((prevState) => ({
      uploadSampleDataFilter: {
        filters: {
          ...(prevState.uploadSampleDataFilter?.filters || {}),
          [columnName]: value || undefined,
        },
      },
    }))
  }

  const TableColumnFiter = ({ filterProps = {}, filterType = "select", customOptions, extra = {} }) => {
    const { column, preFilteredRows } = filterProps
    return (
      <UniversalFilter
        column={column}
        setFilter={column.setFilter}
        preFilteredRows={preFilteredRows}
        updateMetricsWithFilter={updateMetricsWithFilter}
        filterType={filterType}
        customOptions={customOptions}
        isClearable={false}
        {...extra}
      />
    )
  }

  // Extra Table Filtering
  const [startTime, setStartTime] = useState("")
  const [endTime, setEndTime] = useState("")
  const [extraFilters, setExtraFilters] = useState({})
  const [finalExtraFilters, setFinalExtraFilters] = useState({})
  const [tableDataLoading, setTableDataLoading] = useState(true)
  const startKey = "sampling_time__gte",
    endKey = "sampling_time__lte",
    datetimeAPI = "YYYY-MM-DDTHH:mm:ssZZ"
  useEffect(() => {
    const filters = {}
    if (startTime) {
      filters[startKey] = moment(startTime).format(datetimeAPI)
    }
    if (endTime) {
      filters[endKey] = moment(endTime).format(datetimeAPI)
    }
    setExtraFilters(filters)
  }, [startTime, endTime])
  const startFilterChange = extraFilters[startKey] !== finalExtraFilters[startKey]
  const endFilterChange = extraFilters[endKey] !== finalExtraFilters[endKey]
  const hadFilterChange = startFilterChange || endFilterChange

  return (
    <>
      <div className={"row"} style={{ margin: "0", marginBottom: "5px" }}>
        <div className={"col-6"} />
        <div
          className={"row col-6 solid-grey-border"}
          style={{ margin: "0", padding: "5px", borderRadius: "5px" }}
        >
          {[
            [
              { id: "samplemanagement.start.time", defaultMessage: "Start Time" },
              startTime,
              startFilterChange,
              setStartTime,
            ],
            [
              { id: "samplemanagement.end.time", defaultMessage: "End Time" },
              endTime,
              endFilterChange,
              setEndTime,
            ],
          ].map((entry, idx) => (
            <div className={"col-5"} style={{ padding: "0 5px 0 5px" }} key={`datetime${idx}`}>
              <label style={{ whiteSpace: "nowrap", margin: "0", fontSize: "90%" }}>
                {intl.formatMessage(entry[0])}:
              </label>
              &nbsp;
              <input
                type={"datetime-local"}
                className={"form-control form-control-sm"}
                onKeyDown={(e) => {
                  // 8 = Backspace, 46 = Delete - Prevent invalid ts via manual deletions
                  if (e.keyCode === 8 || e.keyCode === 46) {
                    e.preventDefault()
                  }
                }}
                value={entry[1] || ""}
                style={entry[2] ? { border: "3px solid var(--lanzalake-orange)" } : {}}
                disabled={tableDataLoading}
                onChange={(e) => {
                  e.preventDefault()
                  entry[3](e.target.value)
                }}
              />
            </div>
          ))}
          <div className={"col-2"} style={{ padding: "0" }}>
            <Button
              variant={"outline-primary"}
              className={"btn-sm dropdown-full-width"}
              disabled={Object.keys(extraFilters).length === 0 || tableDataLoading}
              onClick={() => {
                setStartTime("")
                setEndTime("")
                setExtraFilters({})
                setFinalExtraFilters({})
                for (const key of [startKey, endKey]) {
                  updateMetricsWithFilter(key, undefined)
                }
              }}
            >
              {intl.formatMessage({ id: "general.clear", defaultMessage: "Clear" })}
            </Button>
            <Button
              variant={"primary"}
              className={"btn-sm dropdown-full-width"}
              disabled={tableDataLoading || !hadFilterChange}
              onClick={() => {
                for (const [key, value] of Object.entries(extraFilters)) {
                  updateMetricsWithFilter(key, value)
                }
                setFinalExtraFilters(extraFilters)
              }}
            >
              {intl.formatMessage({ id: "general.filter", defaultMessage: "Filter" })}
            </Button>
          </div>
        </div>
      </div>
      <LanzaTable
        name={"Sample Data Upload Table"}
        ref={sampleTableRef} // Server Pagination Related
        getDataPage={querySamples} // Server Pagination Related
        setDataStatus={(nonSuccessStatus, dataUnavailableStatus) => {
          setTableDataLoading(nonSuccessStatus)
          setDownloadDisabled(dataUnavailableStatus)
        }}
        className={"table table-bordered table-sm table-hover tbl_lanzatable table-sm"}
        columns={[
          {
            Header: <FormattedMessage id={"samplemanagement.sample.id"} />,
            accessor: "sample_id",
            Cell: ({ row = {} }) => {
              const { original = {} } = row
              const { sample_id, alias } = original
              if (alias) {
                return (
                  <>
                    {sample_id}
                    <br />
                    <span style={{ fontSize: "12px" }}>{`(${alias})`}</span>
                  </>
                )
              } else {
                return sample_id
              }
            },
          },
          {
            Header: <FormattedMessage id={"samplemanagement.sample_round_id"} />,
            accessor: "sample_round",
            smallScreenHide: pageWidth < 1300,
            style: { maxWidth: "100px", width: "100px" },
            Filter: (filterProps) => (
              <TableColumnFiter
                filterProps={filterProps}
                filterType={"text"}
                extra={{ shouldBeNumeric: true }}
              />
            ),
          },
          {
            Header: <FormattedMessage id={"samplemanagement.sample.connection.number"} />,
            accessor: "sampling_point.sample_conn_no",
            style: { maxWidth: "100px", width: "100px" },
            filter: "equals",
            Filter: (filterProps) => (
              <TableColumnFiter
                filterProps={filterProps}
                customOptions={sample_point_data.map((sp) => {
                  return {
                    value: sp["sample_conn_no"],
                    text: sp["sample_conn_no"],
                  }
                })}
              />
            ),
          },
          {
            accessor: "sampling_point",
            Header: <FormattedMessage id={"general.reactors"} />,
            id: "reactors",
            filter: "equals",
            disableSortBy: true,
            style: { minWidth: "150px" },
            Cell: ({ value, row }) => {
              const { original } = row
              const { data_runs = {} } = original
              return (
                <div>
                  {value["reactor_list"].map((reactor) => {
                    return (
                      <div key={`smp${original?.id}${reactor.name}`}>{`${reactor.name} (${
                        data_runs?.[reactor.name]?.name || "-"
                      })`}</div>
                    )
                  })}
                </div>
              )
            },
            Filter: (filterProps) => (
              <TableColumnFiter
                filterProps={filterProps}
                customOptions={getReactors(sample_point_data, "V1").map((r) => {
                  return {
                    value: r,
                    text: r,
                  }
                })}
              />
            ),
          },
          {
            Header: <FormattedMessage id={"samplemanagement.sample.source.type"} />,
            accessor: "sample_source_type.name",
            filter: "equals",
            Filter: (filterProps) => (
              <TableColumnFiter
                filterProps={filterProps}
                customOptions={sample_source_types.map((t) => {
                  return {
                    value: t.display_name,
                    text: t.display_name,
                  }
                })}
              />
            ),
          },
          {
            Header: <FormattedMessage id={"general.actor"} defaultMessage={"Actor"} />,
            accessor: "taken_by",
          },
          {
            Header: <FormattedMessage id={"general.time"} defaultMessage={"Time"} />,
            accessor: "sampling_time",
            disableFilters: true,
            Cell: ({ value }) => <LocaleFormattedDate date={value} />,
          },
          {
            Header: <FormattedMessage id={"samplemanagement.methods"} />,
            accessor: "methods_status",
            filter: "equals",
            disableSortBy: true,
            Cell: ({ value, row }) => {
              const smpData = row?.original
              const methodNum = Object.entries(value).length
              return Object.entries(value).map(([method, info], idx) => (
                <div key={`method_${idx}`}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span>
                      {method} <InfoPopover desc={info.description} placement={"bottom"} />:
                    </span>
                    &nbsp;
                    <ToggleMethodStatus
                      methodInfo={info}
                      name={method}
                      smpID={smpData.id}
                      extraStyle={{ display: "flex", alignItems: "center", fontSize: "90%" }}
                    />
                    &nbsp;
                    <CancelMethodButton
                      isHistorical={smpData.historical}
                      methodInfo={info}
                      name={method}
                      smpID={smpData.id}
                      extraStyle={{ display: "flex", alignItems: "center", fontSize: "90%" }}
                    />
                  </div>
                  {smpData.can_add_method && idx === methodNum - 1 && (
                    <>
                      <hr style={{ marginTop: "6px", marginBottom: "6px" }} />
                      <Button
                        title={intl.formatMessage({
                          id: "samplemanagement.add.methods",
                          defaultMessage: "Add Methods",
                        })}
                        className={"btn btn-sm"}
                        variant={"outline-primary"}
                        disabled={!user_permissions.includes("change_sampleround")}
                        onClick={(event) => {
                          event.preventDefault()
                          getSamplingPointMethods([smpData.sampling_point], true)
                          setSelectedSample(smpData)
                          showModal(setAddMethodModal)
                        }}
                      >
                        <span className={"fa fa-plus"} />
                        &nbsp;&nbsp;
                        <span className={"text-success"}>
                          {intl.formatMessage({
                            id: "general.add",
                            defaultMessage: "Add",
                          })}
                        </span>
                      </Button>
                    </>
                  )}
                </div>
              ))
            },
            Filter: (filterProps) => {
              const uniqueMethods = {}
              sample_point_data
                .map((sp) => sp.methods)
                .forEach((methods) => {
                  Object.values(methods).forEach(
                    (method) =>
                      (uniqueMethods[method.name] = { name: method.name, description: method.description }),
                  )
                })
              const spMethods = Object.values(uniqueMethods)
              return (
                <TableColumnFiter
                  filterProps={filterProps}
                  customOptions={spMethods
                    .map((m) => {
                      return {
                        value: m.name,
                        text: `${m.name} [${m.description}]`,
                      }
                    })
                    .sort((a, b) => {
                      const nameA = a.text.toLowerCase()
                      const nameB = b.text.toLowerCase()
                      if (nameA > nameB) return 1
                      if (nameA < nameB) return -1
                      return 0
                    })}
                />
              )
            },
          },
          {
            Header: <FormattedMessage id={"general.status"} defaultMessage={"Status"} />,
            accessor: "status",
            filter: "equals",
            disableSortBy: true,
            Cell: ({ value, row }) => {
              const sample = row?.original || {}
              return (
                <div>
                  <SampleStatus status={value} />
                  {sample.remarks && (
                    <>
                      &nbsp;
                      <InfoPopover
                        desc={`${intl.formatMessage({ id: "general.remarks", defaultMessage: "Remarks" })}: ${
                          sample.remarks
                        }`}
                        icon={"fa-comments-o"}
                      />
                    </>
                  )}
                </div>
              )
            },
            Filter: (filterProps) => (
              <TableColumnFiter
                filterProps={filterProps}
                customOptions={[
                  {
                    value: "pending_analysis",
                    text: "Pending Analysis",
                    id: "samplemanagement.sample.status.pending_analysis",
                  },
                  {
                    value: "analyzed",
                    text: "Analyzed",
                    id: "samplemanagement.sample.status.analyzed",
                  },
                  {
                    value: "not_analyzed",
                    text: "Not Analyzed",
                    id: "samplemanagement.sample.status.not_analyzed",
                  },
                ]}
              />
            ),
          },
          {
            Header: (
              <FormattedMessage
                id={"samplemanagement.sample.submission.status"}
                defaultMessage={"Submission Status"}
              />
            ),
            accessor: "submission_status",
            filter: "equals",
            disableSortBy: true,
            slimsOnly: true,
            Cell: ({ value }) => <SampleStatus status={value} />,
            Filter: (filterProps) => (
              <TableColumnFiter
                filterProps={filterProps}
                customOptions={[
                  {
                    value: "success",
                    text: "Success",
                    id: "samplemanagement.sample.submission.status.success",
                  },
                  {
                    value: "failed",
                    text: "Failed",
                    id: "samplemanagement.sample.submission.status.failed",
                  },
                ]}
              />
            ),
          },
          {
            Header: "",
            id: "actions",
            style: { minWidth: can_print_skokie ? "120px" : "80px", padding: "10px 5px" },
            className: "text-center",
            accessor: (sample) => {
              const methods_status = Object.values(sample?.["methods_status"] || {}).map((m) => m.status)
              const canSeePrintBtn = can_print_skokie && sample.status === "pending_analysis"
              const canSeeCancelSampleBtn =
                sample.status === "pending_analysis" &&
                !methods_status.includes("in_progress") &&
                !methods_status.includes("analyzed")
              const canSeeEnterDataBtn = sample.status !== "not_analyzed"
              const uploadedFile = getLatestUploadedFile(sample)
              return (
                <>
                  {!!(canSeePrintBtn || canSeeCancelSampleBtn || canSeeEnterDataBtn || uploadedFile) ? (
                    <div>
                      {/* Trigger popup to enter numerical sample data */}
                      {canSeeEnterDataBtn && (
                        <>
                          &nbsp;
                          <Button
                            variant={"outline-primary"}
                            className={"btn btn-sm"}
                            title={intl.formatMessage({ id: "general.enter.data" })}
                            disabled={!user_permissions.includes("change_sampleround")}
                            onClick={(event) => {
                              event.preventDefault()
                              getSamplingPointMethods([sample.sampling_point], true)
                              getCurrentSampleData(sample.id)
                              setSelectedSample(sample)
                              showModal(setEnterDataModal)
                            }}
                          >
                            <span className={"fa fa-pencil-square-o"} />
                          </Button>
                        </>
                      )}
                      {/* Cancel Sample: only if no external deps (e.g. SLIMS) and no current data */}
                      {canSeeCancelSampleBtn && (
                        <>
                          &nbsp;
                          <Button
                            style={{ marginTop: "2px" }}
                            variant={"outline-primary"}
                            className={"btn btn-sm"}
                            title={intl.formatMessage({
                              id: "samplemanagement.sample.data_upload_status.will_not_be_analyzed",
                            })}
                            disabled={
                              sample.has_slims_method || !user_permissions.includes("change_sampleround")
                            }
                            onClick={(event) => updateStatus_notAnalyzed(event, sample)}
                          >
                            <span className={"fa fa-times text-danger"} />
                          </Button>
                        </>
                      )}
                      {/* Skokie labs label printing */}
                      {canSeePrintBtn && (
                        <>
                          &nbsp;
                          <Button
                            style={{ marginTop: "2px" }}
                            variant={"outline-primary"}
                            className={"btn btn-sm"}
                            title={intl.formatMessage({
                              id: "samplemanagement.print.labels",
                            })}
                            onClick={(event) => {
                              event.preventDefault()
                              getCurrentSampleData(sample.id)
                              setSelectedSample(sample)
                              showModal(setPrintLabelsModal)
                            }}
                          >
                            <span className={"fa fa-print"} />
                          </Button>
                        </>
                      )}
                      {!!(canSeePrintBtn || canSeeCancelSampleBtn || canSeeEnterDataBtn) && uploadedFile && (
                        <hr style={{ margin: "10px 0px" }} />
                      )}
                      {uploadedFile && <div>{_render_uploadStatus(sample, uploadedFile)}</div>}
                    </div>
                  ) : (
                    <div>-</div>
                  )}
                </>
              )
            },
            disableSortBy: true,
            disableFilters: true,
          },
        ]
          .filter((col) => (!col.slimsOnly || (is_slims_site && col.slimsOnly)) && !col.smallScreenHide)
          .map((col) => {
            return {
              ...col,
              style: { fontSize: "90%", ...(col["style"] || {}) },
            }
          })}
        allowSorting={true}
        allowFilters={true}
        allowGlobalFilter={true}
        allowRowSelection={false}
        initialTableOptions={{
          sortBy: [{ id: "sampling_time", desc: true }],
          pageSize: 10,
        }}
        extraTableFilters={finalExtraFilters}
        updateMetricsWithFilter={updateMetricsWithFilter}
      />

      {/*---------- Modals ----------*/}
      {selectedSample && enterDataModal && user_permissions.includes("change_sampleround") && (
        <ModalEnterData
          sample={selectedSample}
          show={enterDataModal}
          onHide={() => onHideModal(setEnterDataModal)}
          onSuccess={() => onSuccessfulDataUpdate(setEnterDataModal)}
        />
      )}

      {selectedSample && printLabelsModal && (
        <ModalPrintLabels
          sample={selectedSample}
          show={printLabelsModal}
          printers={availablePrinters}
          setPrinters={setAvailablePrinters}
          onHide={() => onHideModal(setPrintLabelsModal)}
          onSuccess={() => onHideModal(setPrintLabelsModal)}
        />
      )}

      {selectedSample && uploadDataModal && (
        <ModalUploadDataFile
          sample={selectedSample}
          show={uploadDataModal}
          onHide={() => onHideModal(setUploadDataModal)}
          onSuccess={() => onSuccessfulDataUpdate(setUploadDataModal)}
        />
      )}

      {selectedSample && viewUploadLogsModal && (
        <ModalViewDataUploadLogs
          sample={selectedSample}
          show={viewUploadLogsModal}
          onHide={() => onHideModal(setViewUploadLogsModal)}
        />
      )}

      {selectedSample && addMethodModal && user_permissions.includes("change_sampleround") && (
        <ModalAddMethod
          sample={selectedSample}
          show={addMethodModal}
          onHide={() => onHideModal(setAddMethodModal)}
          onSubmit={onMethodsAdded}
        />
      )}
      {/*---------- /Modals ----------*/}
    </>
  )
}

export default withSampleConfig(TableSampleAnalysisStatus)

TableSampleAnalysisStatus.propTypes = {
  querySamples: PropTypes.func.isRequired, // SampleConfigProvider - Function
  sample_not_analyzed_status: PropTypes.string.isRequired, // SampleConfigProvider - API_STATUS (String)
  reloadSampleTable: PropTypes.func.isRequired, // SampleConfigProvider - Function
  updateMetrics: PropTypes.func.isRequired, // SampleConfigProvider - Function
  markSampleAsNotAnalyzed: PropTypes.func.isRequired, // SampleConfigProvider - Function
  reloadSampleRoundsTable: PropTypes.func.isRequired, // SampleConfigProvider - Function
  getCurrentSampleData: PropTypes.func.isRequired, // SampleConfigProvider - Function
}
